import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { promptsKeys } from "@/lib/queries";
import { getLocalUserFacingApiMessage, passThroughMessage } from "@/lib/api-client";
import {
  createPrompt,
  deletePrompt,
  fetchPrompt,
  updatePrompt,
  type PromptDto,
} from "@/features/prompts/api/prompts.api";

export interface DuplicatePromptInput {
  source: PromptDto;
  name: string;
  usedIn: string;
  tenantId: string | null;
}

/**
 * Row-level prompt actions shared by the table menu and the editor drawer.
 * The API rejects both deactivating and deleting a global prompt, so callers
 * disable those entries for `tenantId === null` rather than relying on the error.
 */
export function usePromptActions() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: promptsKeys.all });
  };

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: "ACTIVE" | "INACTIVE" }) =>
      updatePrompt(id, { status }),
    onSuccess: (prompt) => {
      invalidate();
      toast.success(
        prompt.status === "ACTIVE"
          ? t("prompts.actions.toast.activated", "Prompt activated")
          : t("prompts.actions.toast.deactivated", "Prompt deactivated"),
      );
    },
    onError: (err) => {
      const message = getLocalUserFacingApiMessage(err, passThroughMessage);
      if (message) toast.error(message);
    },
    meta: { skipGlobalError: true },
  });

  const remove = useMutation({
    mutationFn: (id: string) => deletePrompt(id),
    onSuccess: () => {
      invalidate();
      toast.success(t("prompts.actions.toast.deleted", "Prompt deleted"));
    },
    onError: (err) => {
      const message = getLocalUserFacingApiMessage(err, passThroughMessage);
      if (message) toast.error(message);
    },
    meta: { skipGlobalError: true },
  });

  const duplicate = useMutation({
    // List DTOs truncate latestVersion.content to a 100-char preview — always
    // re-fetch the detail endpoint so the copy persists the full template.
    mutationFn: async ({ source, name, usedIn, tenantId }: DuplicatePromptInput) => {
      const detail = await fetchPrompt(source.id);
      return createPrompt({
        name,
        usedIn,
        tenantId,
        content: detail.latestVersion?.content ?? "",
        model: detail.latestVersion?.model ?? null,
        taskType: detail.taskType,
        variables: detail.latestVersion?.variables ?? [],
      });
    },
    onSuccess: (result) => {
      invalidate();
      toast.success(
        t("prompts.actions.toast.duplicated", 'Created "{{name}}"', {
          name: result.prompt.name,
        }),
      );
      if (result.globalFallbackCreated) {
        toast.info(
          t(
            "prompts.editor.toast.globalFallbackCreated",
            'A global fallback prompt was also created for "{{usedIn}}"',
            { usedIn: result.prompt.usedIn },
          ),
        );
      }
    },
    onError: (err) => {
      const message = getLocalUserFacingApiMessage(err, passThroughMessage);
      if (message) toast.error(message);
    },
    meta: { skipGlobalError: true },
  });

  return { setStatus, remove, duplicate };
}
